import { getUserCountryHint } from '@/utils/userCountryHint'

export const PAYMENT_METHOD_AIX_PREFIX = 'aix:'

const AIX_WAY_CODE_CATALOG = [
  'AIX_RUB_SBP',
  'AIX_RUB_SBERPAY',
  'AIX_RUB_TPAY',
  'AIX_RUB_SBERCARD',
  'AIX_RUB_CC_PAY',
]

function parseEnvWayCodes() {
  const raw = String(process.env.VUE_APP_AIX_WAY_CODES || '').trim()
  if (!raw) return null
  const codes = raw.split(',').map((item) => item.trim()).filter(Boolean)
  return codes.length ? codes : null
}

/** Fallback when API config is unavailable; override via VUE_APP_AIX_WAY_CODES */
export const AIX_WAY_CODES = parseEnvWayCodes() || AIX_WAY_CODE_CATALOG

const AIX_I18N_KEYS = {
  AIX_RUB_SBP: 'Payment_Method_Aix_SBP',
  AIX_RUB_SBERPAY: 'Payment_Method_Aix_SberPay',
  AIX_RUB_TPAY: 'Payment_Method_Aix_TPay',
  AIX_RUB_SBERCARD: 'Payment_Method_Aix_SberCard',
  AIX_RUB_CC_PAY: 'Payment_Method_Aix_MirCard',
}

const AIX_WAY_CODE_TO_PAY_METHOD = {
  AIX_RUB_SBP: 151,
  AIX_RUB_SBERPAY: 152,
  AIX_RUB_TPAY: 153,
  AIX_RUB_SBERCARD: 154,
  AIX_RUB_CC_PAY: 155,
}

let cachedConfig = null
let configPromise = null

export function toAixPaymentMethod(wayCode) {
  return `${PAYMENT_METHOD_AIX_PREFIX}${wayCode}`
}

export function isAixPaymentMethod(method) {
  return String(method || '').startsWith(PAYMENT_METHOD_AIX_PREFIX)
}

export function getAixWayCode(method) {
  if (!isAixPaymentMethod(method)) return null
  return String(method).slice(PAYMENT_METHOD_AIX_PREFIX.length)
}

export function getAixPaymentMethodLabelKey(method) {
  const wayCode = getAixWayCode(method)
  return AIX_I18N_KEYS[wayCode] || 'Payment_Method_Aix'
}

export function getAixPaymentMethodCode(method) {
  const wayCode = getAixWayCode(method)
  return wayCode ? AIX_WAY_CODE_TO_PAY_METHOD[wayCode] : null
}

export function isSameAixPaymentMethod(selectedMethod, orderPaymentMethod) {
  const code = getAixPaymentMethodCode(selectedMethod)
  return code != null && Number(orderPaymentMethod) === code
}

export function isAixPayEligible({ locale, countryHint } = {}) {
  const localeValue = String(locale || '').toLowerCase()
  const hint = countryHint || getUserCountryHint()
  return localeValue.startsWith('ru') || String(hint || '').toUpperCase() === 'RU'
}

export async function loadAixPayConfig(api) {
  if (cachedConfig) return cachedConfig
  if (configPromise) return configPromise

  configPromise = api.payment.getAixPayConfig({ skipLoading: true })
    .then((res) => {
      const data = res?.data?.data || {}
      cachedConfig = {
        enabled: Boolean(data.enabled),
        currency: data.currency || 'RUB',
        rubExchangeRate: data.rub_exchange_rate ?? null,
        wayCodes: Array.isArray(data.way_codes)
          ? data.way_codes.map((item) => item.code).filter(Boolean)
          : [],
      }
      return cachedConfig
    })
    .catch(() => {
      cachedConfig = { enabled: false, currency: 'RUB', rubExchangeRate: null, wayCodes: [] }
      return cachedConfig
    })
    .finally(() => {
      configPromise = null
    })

  return configPromise
}

export function getCachedAixPayConfig() {
  return cachedConfig
}

/** 按后端 AIX 结算规则将 USD 估算为 RUB（与下单后 amount_rub 一致） */
export function estimateRubFromUsd(amountUsd, rubExchangeRate) {
  const usd = Number(amountUsd)
  const rate = Number(rubExchangeRate)
  if (!Number.isFinite(usd) || !Number.isFinite(rate) || rate <= 0) {
    return null
  }
  return (Math.round(usd * rate * 100) / 100).toFixed(2)
}

/** 俄语/俄罗斯用户是否展示 AIX 本地支付入口 */
export function shouldOfferAixPaymentMethods(options = {}) {
  return isAixPayEligible(options)
}

/** 后端是否已完整配置并可实际发起 AIX 支付 */
export async function isAixPayAvailable(api, options = {}) {
  const config = await loadAixPayConfig(api)
  return Boolean(config.enabled) && isAixPayEligible(options)
}

export function buildPendingAixPayment({
  orderNo,
  paymentLink,
  amountRub,
  productName,
  wayCode,
  expiredInMinutes = 30,
}) {
  return {
    orderNo,
    paymentLink,
    amountRub,
    productName,
    paymentMethod: toAixPaymentMethod(wayCode),
    wayCode,
    expiredInMinutes,
    createdAt: Date.now(),
  }
}

export async function createAixPayment(api, orderNo, wayCode) {
  const payRes = await api.payment.createAixPaymentOrder({
    order_no: orderNo,
    way_code: wayCode,
  })
  const data = payRes?.data?.data
  if (!data?.payment_link || payRes?.data?.status !== '0') {
    return {
      ok: false,
      message: payRes?.data?.message || payRes?.data?.msg || 'Payment failed',
    }
  }
  return {
    ok: true,
    paymentLink: data.payment_link,
    amountRub: data.amount_rub,
    wayCode: data.way_code || wayCode,
    expiredInMinutes: data.expired_in_minutes ?? 30,
  }
}
